import {
  SyncJobStatus,
  SyncJobType,
  SyncJobsFilters,
  SyncJobsSortDirection,
  SyncJobsSortField,
} from "@/types/pms";

const SYNC_JOBS_SORT_FIELDS: SyncJobsSortField[] = ["createdAt", "status"];
const SYNC_JOBS_SORT_DIRECTIONS: SyncJobsSortDirection[] = ["asc", "desc"];

const isSyncJobStatus = (value: string): value is SyncJobStatus =>
  Object.values(SyncJobStatus).includes(value as SyncJobStatus);

const isSyncJobType = (value: string): value is SyncJobType =>
  Object.values(SyncJobType).includes(value as SyncJobType);

const isSortField = (value: string): value is SyncJobsSortField =>
  SYNC_JOBS_SORT_FIELDS.includes(value as SyncJobsSortField);

const isSortDirection = (value: string): value is SyncJobsSortDirection =>
  SYNC_JOBS_SORT_DIRECTIONS.includes(value as SyncJobsSortDirection);

export const DEFAULT_SYNC_JOBS_FILTERS: SyncJobsFilters = {
  page: 1,
  pageSize: 20,
  sortBy: "createdAt",
  sortDirection: "desc",
};

const parsePositiveInt = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const parseSyncJobsFiltersFromParams = (
  params: URLSearchParams,
): SyncJobsFilters => {
  const status = params.get("status");
  const type = params.get("type");
  const sortBy = params.get("sortBy");
  const sortDirection = params.get("sortDirection");
  const from = params.get("from");
  const to = params.get("to");
  const page = parsePositiveInt(params.get("page"));
  const pageSize = parsePositiveInt(params.get("pageSize"));

  return {
    ...DEFAULT_SYNC_JOBS_FILTERS,
    ...(page !== undefined ? { page } : {}),
    ...(pageSize !== undefined && pageSize <= 100 ? { pageSize } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(status && isSyncJobStatus(status) ? { status } : {}),
    ...(type && isSyncJobType(type) ? { type } : {}),
    ...(sortBy && isSortField(sortBy) ? { sortBy } : {}),
    ...(sortDirection && isSortDirection(sortDirection)
      ? { sortDirection }
      : {}),
  };
};

export const syncJobsFiltersToParams = (
  filters: SyncJobsFilters,
): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.page && filters.page !== DEFAULT_SYNC_JOBS_FILTERS.page) {
    params.set("page", String(filters.page));
  }
  if (
    filters.pageSize &&
    filters.pageSize !== DEFAULT_SYNC_JOBS_FILTERS.pageSize
  ) {
    params.set("pageSize", String(filters.pageSize));
  }
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.status) params.set("status", filters.status);
  if (filters.type) params.set("type", filters.type);
  if (filters.sortBy && filters.sortBy !== DEFAULT_SYNC_JOBS_FILTERS.sortBy) {
    params.set("sortBy", filters.sortBy);
  }
  if (
    filters.sortDirection &&
    filters.sortDirection !== DEFAULT_SYNC_JOBS_FILTERS.sortDirection
  ) {
    params.set("sortDirection", filters.sortDirection);
  }
  return params;
};

export const isoDateOnly = (value: string | undefined): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

export const dateInputToIso = (
  value: string,
  endOfDay: boolean,
): string | undefined => {
  if (!value) return undefined;
  const suffix = endOfDay ? "T23:59:59.999Z" : "T00:00:00.000Z";
  return `${value}${suffix}`;
};
